import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { queryClient } from '@/lib/query-client';
import { Sentry } from '@/lib/sentry';
import { Link, useRouter } from '@tanstack/react-router';
import { AlertTriangle, Home, RotateCcw } from 'lucide-react';
import { useEffect } from 'react';

interface RouteErrorPageProps {
  error: unknown;
  reset?: () => void;
}

/**
 * Fallback de erro pro TanStack Router (`defaultErrorComponent`). Substitui o
 * "Something went wrong" genérico por UI que dá ao user duas saídas:
 * 1. **Tentar novamente** — `router.invalidate()` + `qc.clear()` re-monta tudo
 *    (equivale a um F5 controlado, sem recarregar a página inteira).
 * 2. **Ir pro Dashboard** — escapa de qualquer state corrompido pra rota
 *    raíz que tipicamente está saudável.
 *
 * Em DEV, mostra a mensagem do erro pra debug. Em PROD, mensagem genérica
 * pra não vazar info técnica pro usuário final.
 */
export function RouteErrorPage({ error, reset }: RouteErrorPageProps) {
  const router = useRouter();
  const isDev = import.meta.env.DEV;
  const message = error instanceof Error ? error.message : String(error);

  // Sentry: captura erro pra dashboard de issues. No-op se VITE_SENTRY_DSN
  // não setado. useEffect garante 1 captura por mount (não a cada re-render).
  useEffect(() => {
    if (error instanceof Error) {
      Sentry.captureException(error, {
        tags: { source: 'RouteErrorPage' },
      });
    }
  }, [error]);

  function handleRetry(): void {
    // Limpa cache TanStack Query + invalida router → re-fetch tudo.
    // Mais limpo que F5 (sem reload da página) e em geral resolve estado stale.
    queryClient.clear();
    void router.invalidate();
    if (reset) reset();
  }

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <Card className="w-full max-w-lg border-destructive/40">
        <CardHeader className="space-y-2">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <CardTitle className="text-center text-xl">Algo deu errado</CardTitle>
          <p className="text-center text-sm text-muted-foreground">
            A página não conseguiu renderizar. Geralmente é estado em cache desatualizado — tente de
            novo abaixo.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {isDev ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs">
              <div className="font-mono font-semibold text-destructive">DEV — detalhes:</div>
              <div className="mt-1 break-all font-mono text-destructive/80">{message}</div>
            </div>
          ) : null}

          <div className="flex flex-col gap-2 sm:flex-row">
            <Button onClick={handleRetry} className="flex-1">
              <RotateCcw className="h-4 w-4" />
              Tentar novamente
            </Button>
            <Button asChild variant="outline" className="flex-1">
              <Link to="/">
                <Home className="h-4 w-4" />
                Ir pro Dashboard
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
